/**
 * Abrechnungs-Tutorial
 *
 * Führt durch den Abrechnungsprozess:
 * BillingView (Übersicht) → BillingDetailView (Detail + Einreichen)
 *
 * Abgedeckte Prozesse: P18 (Übersicht), P19 (Detail + Einreichen), P20 (Sperre)
 * — siehe docs/PROZESSLANDKARTE.md.
 */

import type { TutorialDefinition, TutorialStep } from './types'
import { useBillingStore } from '@/stores/billing.store'
import { InstructorAccountStatus } from '@/types/api.types'

function createSteps(): TutorialStep[] {
  const billingStore = useBillingStore()

  // Der Billing-Store hat bewusst KEINEN IndexedDB-Cache (Abrechnung ist online-only).
  // Startet der Nutzer das Tutorial, ohne die Abrechnung vorher geöffnet zu haben,
  // ist die Liste leer — dann gibt es die verkürzte Tour, die auf der Übersicht endet.
  const account = billingStore.currentPeriodAccount ?? billingStore.accounts[0]

  const steps: TutorialStep[] = [
    {
      popover: {
        title: 'Abrechnung einreichen',
        description:
          'Diese Anleitung zeigt dir, wo du deine geleisteten Stunden findest und wie du sie einreichst.',
      },
    },
    {
      navigateTo: '/billing',
      element: '[data-tutorial="billing-current"]',
      popover: {
        title: 'Der aktuelle Zeitraum',
        description:
          'Oben steht immer der laufende Zeitraum mit der Anzahl der Kurse, den Stunden und dem Betrag. Darunter folgen ältere Zeiträume sowie Zeiträume, für die du noch keine Abrechnung angelegt hast.',
        position: 'bottom',
      },
    },
  ]

  if (account) {
    steps.push(
      {
        navigateTo: `/billing/${account.id}`,
        element: '[data-tutorial="billing-stepper"]',
        popover: {
          title: 'Der Status',
          description:
            'Die Leiste zeigt den Stand der Abrechnung: Entwurf, Eingereicht, Genehmigt, Freigegeben, Ausgezahlt. Ändern kannst du eine Abrechnung nur im Status Entwurf.',
          position: 'bottom',
        },
      },
      {
        element: '[data-tutorial="billing-summary"]',
        popover: {
          title: 'Deine Stunden',
          description:
            'Die Übersicht trennt deine Stunden als Kursleiter von denen als Co-Trainer und nennt den Gesamtbetrag. Darunter kannst du jeden Kurs aufklappen und die einzelnen Termine prüfen.',
          position: 'top',
        },
      },
      // Die Aktionsbuttons existieren nur im Entwurf (v-if in BillingDetailView).
      // Ein fester Element-Step würde bei jedem anderen Status still auf ein
      // zentriertes Popover zurückfallen — deshalb hier zwei Varianten. Im Entwurf
      // hebt der Step die Buttons hervor, statt sie mit dem Popover zu verdecken.
      account.status === InstructorAccountStatus.Draft
        ? {
            element: '[data-tutorial="billing-actions"]',
            popover: {
              title: 'Einreichen sperrt die Termine',
              description:
                'Mit „Einreichen" schickst du die Abrechnung ab. Danach kannst du die Anwesenheit der abgerechneten Termine nicht mehr ändern. Prüfe sie deshalb vorher.',
              position: 'top',
            },
          }
        : {
            popover: {
              title: 'Einreichen sperrt die Termine',
              description:
                'Im Status Entwurf stehen unter der Kursliste die Schaltflächen „Neu berechnen", „Löschen" und „Einreichen". Nach dem Einreichen kannst du die Anwesenheit der abgerechneten Termine nicht mehr ändern.',
            },
          },
      {
        popover: {
          title: 'Einreichung zurücknehmen',
          description:
            'Solange die Abrechnung noch nicht genehmigt ist, kannst du sie zurücknehmen. Sie wechselt dann zurück in den Status Entwurf, und du kannst die Termine wieder bearbeiten.',
        },
      },
      {
        popover: {
          title: 'Zwei Einschränkungen',
          description:
            'Einreichen ist erst kurz vor Ende des Zeitraums möglich: bei einem Quartal 21 Tage vorher, bei einem Monat 7 Tage vorher. Liegt der letzte Termin noch in der Zukunft, fragt die App vor dem Einreichen nach.',
        },
      }
    )
  } else {
    steps.push({
      popover: {
        title: 'Noch keine Abrechnung',
        description:
          'Lege zuerst über „Erstellen" eine Abrechnung an. Danach siehst du hier deine Stunden je Kurs und kannst sie einreichen.',
      },
    })
  }

  return steps
}

export const billingTutorial: TutorialDefinition = {
  id: 'billing',
  title: 'Abrechnung einreichen',
  description: 'Stunden prüfen, Abrechnung einreichen und bei Bedarf zurücknehmen.',
  icon: 'pi pi-file-edit',
  steps: createSteps,
}
